import type { CompletionView } from "@ec2/contracts";
import type { ApiClient } from "./api";

export interface PollOptions {
  /** Base delay between polls (grows 1.5× up to 4× base). */
  intervalMs?: number;
  /** Give up (still "processing") after this long — AI validation can exceed 30 s. */
  maxWaitMs?: number;
  onTick?: (completion: CompletionView | null) => void;
  signal?: AbortSignal;
}

/**
 * Poll quest.completion until the status leaves "processing" (photo flow beat 4).
 * Tolerates long validation; returns the last seen completion (possibly still
 * processing — the quest card keeps carrying the state, flow is non-blocking).
 */
export async function pollCompletion(
  client: ApiClient,
  questId: string,
  opts: PollOptions = {},
): Promise<CompletionView | null> {
  const base = opts.intervalMs ?? 2500;
  const deadline = Date.now() + (opts.maxWaitMs ?? 120_000);
  let delay = base;
  let latest: CompletionView | null = null;
  for (;;) {
    if (opts.signal?.aborted) return latest;
    latest = await client.quest.completion.query({ questId });
    opts.onTick?.(latest);
    if (latest && latest.status !== "processing") return latest;
    if (Date.now() + delay > deadline) return latest;
    await sleep(delay, opts.signal);
    delay = Math.min(delay * 1.5, base * 4);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    function done() {
      signal?.removeEventListener("abort", done);
      clearTimeout(timer);
      resolve();
    }
    signal?.addEventListener("abort", done);
  });
}
